import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  FaShieldAlt, FaHistory, FaUserShield, FaBan, FaSearch, FaTimes,
} from 'react-icons/fa';
import {
  getAuditLogs,
  getAuditLogStats,
} from '../../../api/services/adminService';
import {
  SectionHeader, StatCard, Badge, EmptyState, Alert, Pagination,
} from './AdminUI';
import './AuditLogs.css';

const PAGE_SIZE = 25;

const ACTION_LABELS = {
  SUSPEND_USER: 'Suspend user',
  UNSUSPEND_USER: 'Unsuspend user',
  APPROVE_UNSUSPENSION: 'Approve unsuspension',
  REJECT_UNSUSPENSION: 'Reject unsuspension',
  GRANT_ROLE: 'Grant role',
  REVOKE_ROLE: 'Revoke role',
  APPROVE_ROLE_REQUEST: 'Approve role request',
  REJECT_ROLE_REQUEST: 'Reject role request',
  EDIT_USER_ROLE: 'Edit user role',
  DELETE_USER: 'Delete user',
  IMPERSONATE_USER: 'Impersonate user',
  END_IMPERSONATION: 'End impersonation',
  APPROVE_KYC: 'Approve KYC',
  REJECT_KYC: 'Reject KYC',
  APPROVE_KYB: 'Approve KYB',
  REJECT_KYB: 'Reject KYB',
  CREATE_PLAN: 'Create plan',
  UPDATE_PLAN: 'Update plan',
  DELETE_PLAN: 'Delete plan',
  GRANT_SUBSCRIPTION: 'Grant subscription',
  CANCEL_SUBSCRIPTION: 'Cancel subscription',
  UPDATE_SETTINGS: 'Update settings',
  UPSERT_INTEGRATION_CONFIG: 'Upsert integration config',
  DELETE_INTEGRATION_CONFIG: 'Delete integration config',
  UPDATE_SYSTEM_AGREEMENT: 'Update system agreement',
  POST_TO_FACEBOOK: 'Post to Facebook',
  CREATE_UNIT_CATEGORY: 'Create unit category',
  UPDATE_UNIT_CATEGORY: 'Update unit category',
  DELETE_UNIT_CATEGORY: 'Delete unit category',
  CREATE_UNIT_TYPE: 'Create unit type',
  UPDATE_UNIT_TYPE: 'Update unit type',
  DELETE_UNIT_TYPE: 'Delete unit type',
  CREATE_UNIT_SUBCATEGORY: 'Create unit subcategory',
  UPDATE_UNIT_SUBCATEGORY: 'Update unit subcategory',
  DELETE_UNIT_SUBCATEGORY: 'Delete unit subcategory',
  BROADCAST_NOTIFICATION: 'Broadcast notification',
  SEND_TEST_NOTIFICATION: 'Send test notification',
  UPDATE_REPORT_STATUS: 'Update report status',
};

const ACTION_OPTIONS = Object.keys(ACTION_LABELS).sort((a, b) =>
  ACTION_LABELS[a].localeCompare(ACTION_LABELS[b]));

const formatDateTime = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return '—';
  }
};

const actionBadge = (action) => {
  const success = ['APPROVE', 'GRANT', 'CREATE', 'UNSUSPEND', 'UPDATE', 'POST'].some((p) => action.includes(p));
  const danger = ['REJECT', 'DELETE', 'SUSPEND', 'REVOKE', 'CANCEL'].some((p) => action.includes(p));
  if (danger) return 'danger';
  if (success) return 'success';
  return 'info';
};

const AuditLogs = () => {
  const [logs, setLogs] = useState([]);
  const [pageData, setPageData] = useState(null);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);

  const [filters, setFilters] = useState({ action: '', adminUserId: '', targetUserId: '', from: '', to: '' });
  const [appliedFilters, setAppliedFilters] = useState({ action: '', adminUserId: '', targetUserId: '', from: '', to: '' });

  const [selected, setSelected] = useState(null);

  const loadLogs = useCallback(async (pageIndex, f) => {
    setLoading(true);
    setError(null);
    try {
      const opts = { page: pageIndex, size: PAGE_SIZE };
      if (f.action) opts.action = f.action;
      if (f.adminUserId) opts.adminUserId = f.adminUserId;
      if (f.targetUserId) opts.targetUserId = f.targetUserId;
      if (f.from) opts.from = f.from;
      if (f.to) opts.to = f.to;
      const res = await getAuditLogs(opts);
      const inner = res?.data;
      setPageData(inner || null);
      setLogs(Array.isArray(inner?.content) ? inner.content : []);
    } catch (err) {
      setError(err?.message || 'Failed to load audit logs');
      setLogs([]);
      setPageData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const res = await getAuditLogStats();
      setStats(res?.data || null);
    } catch {
      setStats(null);
    } finally {
      setStatsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadLogs(page, appliedFilters);
  }, [page, appliedFilters, loadLogs]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const applyFilters = (e) => {
    e.preventDefault();
    setPage(0);
    setAppliedFilters(filters);
  };

  const resetFilters = () => {
    setFilters({ action: '', adminUserId: '', targetUserId: '', from: '', to: '' });
    setAppliedFilters({ action: '', adminUserId: '', targetUserId: '', from: '', to: '' });
    setPage(0);
  };

  const totalPages = pageData?.totalPages ?? 0;
  const totalElements = pageData?.totalElements ?? 0;

  const statCards = useMemo(() => {
    const s = stats || {};
    return [
      { icon: <FaHistory />, iconBg: 'var(--admin-primary-light)', label: 'Total actions', value: s.totalActions ?? 0, trend: 'All time', trendType: 'neutral' },
      { icon: <FaShieldAlt />, iconBg: 'var(--admin-info-light)', label: 'Last 24 hours', value: s.actionsLast24h ?? 0, trend: 'Recent activity', trendType: 'info' },
      { icon: <FaUserShield />, iconBg: 'var(--admin-success-light)', label: 'Last 7 days', value: s.actionsLast7d ?? 0, trend: 'Weekly volume', trendType: 'neutral' },
      { icon: <FaBan />, iconBg: 'var(--admin-danger-light)', label: 'User suspensions', value: s.suspendUserCount ?? 0, trend: 'Total', trendType: 'warning' },
    ];
  }, [stats]);

  return (
    <div className="audit-logs-admin">
      <SectionHeader
        title="Audit Logs"
        subtitle="Immutable record of every administrative action performed on the platform."
        action={
          <button type="button" className="admin-btn admin-btn-outline" onClick={() => { loadLogs(page, appliedFilters); loadStats(); }} disabled={loading}>
            {loading ? 'Refreshing…' : 'Refresh'}
          </button>
        }
      />

      {error && (
        <Alert type="error" onClose={() => setError(null)}>
          <strong>Couldn't load audit logs.</strong> {error}
        </Alert>
      )}

      <div className="admin-grid admin-grid-stats" style={{ marginBottom: 20 }}>
        {statCards.map((s) => (
          <StatCard key={s.label} {...s} loading={statsLoading} />
        ))}
      </div>

      <form className="admin-card audit-filters" onSubmit={applyFilters}>
        <div className="audit-filters-grid">
          <label className="audit-field">
            <span>Action</span>
            <select value={filters.action} onChange={(e) => setFilters((f) => ({ ...f, action: e.target.value }))}>
              <option value="">All actions</option>
              {ACTION_OPTIONS.map((a) => (
                <option key={a} value={a}>{ACTION_LABELS[a]}</option>
              ))}
            </select>
          </label>
          <label className="audit-field">
            <span>Admin user ID</span>
            <input type="text" placeholder="UUID" value={filters.adminUserId} onChange={(e) => setFilters((f) => ({ ...f, adminUserId: e.target.value }))} />
          </label>
          <label className="audit-field">
            <span>Target user ID</span>
            <input type="text" placeholder="UUID" value={filters.targetUserId} onChange={(e) => setFilters((f) => ({ ...f, targetUserId: e.target.value }))} />
          </label>
          <label className="audit-field">
            <span>From</span>
            <input type="date" value={filters.from} onChange={(e) => setFilters((f) => ({ ...f, from: e.target.value }))} />
          </label>
          <label className="audit-field">
            <span>To</span>
            <input type="date" value={filters.to} onChange={(e) => setFilters((f) => ({ ...f, to: e.target.value }))} />
          </label>
        </div>
        <div className="audit-filters-actions">
          <button type="submit" className="admin-btn admin-btn-primary"><FaSearch /> Apply</button>
          <button type="button" className="admin-btn admin-btn-ghost" onClick={resetFilters}>Reset</button>
        </div>
      </form>

      <div className="admin-table-wrap" style={{ marginTop: 20 }}>
        <table className="admin-table">
          <thead>
            <tr>
              <th>Action</th>
              <th>Administrator</th>
              <th>Target</th>
              <th>Description</th>
              <th>IP address</th>
              <th>When</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7}><div className="admin-skeleton" style={{ height: 40 }} /></td></tr>
            ) : logs.length === 0 ? (
              <tr><td colSpan={7}><EmptyState icon="📋" title="No audit entries" text="No actions match your filters." /></td></tr>
            ) : (
              logs.map((log) => (
                <tr key={log.id}>
                  <td><Badge status={actionBadge(log.action)}>{ACTION_LABELS[log.action] || log.action}</Badge></td>
                  <td>{log.adminEmail || log.adminUserId || '—'}</td>
                  <td>{log.targetEmail || log.targetUserId || '—'}</td>
                  <td className="audit-desc-cell" title={log.description}>{log.description || '—'}</td>
                  <td>{log.ipAddress || '—'}</td>
                  <td>{formatDateTime(log.createdAt)}</td>
                  <td>
                    <button type="button" className="admin-btn admin-btn-ghost" onClick={() => setSelected(log)}>View</button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {!loading && logs.length > 0 && (
        <Pagination
          page={page}
          totalPages={totalPages}
          totalElements={totalElements}
          pageSize={PAGE_SIZE}
          onPageChange={setPage}
        />
      )}

      {selected && (
        <div className="audit-modal-overlay" onClick={() => setSelected(null)} role="presentation">
          <div className="audit-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <div className="audit-modal-head">
              <div>
                <h3>{ACTION_LABELS[selected.action] || selected.action}</h3>
                <p>{formatDateTime(selected.createdAt)}</p>
              </div>
              <button type="button" className="audit-modal-close" onClick={() => setSelected(null)} aria-label="Close"><FaTimes /></button>
            </div>
            <div className="audit-modal-body">
              <div className="audit-modal-row">
                <span>Administrator</span>
                <strong>{selected.adminEmail || '—'}</strong>
              </div>
              <div className="audit-modal-row">
                <span>Admin user ID</span>
                <code>{selected.adminUserId || '—'}</code>
              </div>
              <div className="audit-modal-row">
                <span>Target user</span>
                <strong>{selected.targetEmail || '—'}</strong>
              </div>
              <div className="audit-modal-row">
                <span>Target user ID</span>
                <code>{selected.targetUserId || '—'}</code>
              </div>
              <div className="audit-modal-row">
                <span>IP address</span>
                <strong>{selected.ipAddress || '—'}</strong>
              </div>
              <div className="audit-modal-row audit-modal-row-block">
                <span>Description</span>
                <p>{selected.description || '—'}</p>
              </div>
              {selected.metadataJson && (
                <div className="audit-modal-row audit-modal-row-block">
                  <span>Metadata</span>
                  <pre className="audit-metadata">{(() => {
                    try { return JSON.stringify(JSON.parse(selected.metadataJson), null, 2); }
                    catch { return selected.metadataJson; }
                  })()}</pre>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuditLogs;
